import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Sparkles, Upload, Copy, Check, Loader2, Trash2, RefreshCw, Video, Image as ImageIcon,
  Wand2, Send, Edit3, X, Instagram, Facebook, Info, Link2, Unlink, TrendingUp, Eye,
  Heart, MessageCircle, Bookmark, Share2, Users, BarChart3, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { key: "ospite",     label: "🍷 L'ospite protagonista",   hint: "Tavoli, brindisi, spa, camere, dettagli piatto" },
  { key: "territorio", label: "🏞️ Territorio marchigiano", hint: "Borghi, sagre, Sibillini, ricette regionali" },
  { key: "backstage",  label: "👥 Dietro le quinte",         hint: "Chef, orto, cavalli, staff, storie" },
];

const STATUS_META = {
  draft:     { label: "Bozza",       cls: "bg-neutral-100 text-neutral-700 border-neutral-300" },
  ready:     { label: "Pronto",      cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  scheduled: { label: "Programmato", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  published: { label: "Pubblicato",  cls: "bg-blue-100 text-blue-800 border-blue-300" },
  archived:  { label: "Archiviato",  cls: "bg-neutral-200 text-neutral-600 border-neutral-300" },
};

const authHeaders = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SocialStudioEditor() {
  return (
    <div className="space-y-6" data-testid="social-studio-editor">
      <MetaConnectSection />
      <MetaDashboardSection />
      <GeneratorSection />
      <LibrarySection />
    </div>
  );
}

// -------------------- META CONNECT --------------------
function MetaConnectSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/social/meta/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Handle callback redirect params
    const p = new URLSearchParams(window.location.search);
    if (p.get("meta_connected") === "1") {
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 500);
    } else if (p.get("meta_error")) {
      const err = p.get("meta_error");
      window.history.replaceState({}, "", window.location.pathname);
      alert("Connessione Meta fallita: " + err);
    }
  }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/admin/social/meta/start`, { headers: authHeaders() });
      window.location.href = r.data.authorize_url;
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare l'account Meta? Dovrai riautenticare per pubblicare.")) return;
    try {
      await axios.post(`${API}/admin/social/meta/disconnect`, {}, { headers: authHeaders() });
      load();
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <section className="rounded-2xl bg-white border border-[#e8dcc8] shadow-sm p-5 sm:p-6" data-testid="meta-connect">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-pink-500 via-fuchsia-500 to-indigo-500 text-white flex items-center justify-center">
          <Link2 className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-lg font-bold text-[#2b1410]">Connessione Meta (Instagram + Facebook)</h3>
          <p className="text-xs text-[#7c5a4e]">Collega i tuoi account per pubblicare e monitorare le performance direttamente da qui</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#7c5a4e]"><Loader2 className="h-4 w-4 animate-spin" />Verifica connessione…</div>
      ) : status?.connected ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3">
            <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <Check className="h-4 w-4" />
            </div>
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">Connesso a Meta</div>
              <div className="text-emerald-800 text-xs mt-0.5">
                <Facebook className="inline h-3 w-3 mr-1" /> {status.page_name || status.page_id}
                {status.ig_username && <> · <Instagram className="inline h-3 w-3 mx-1" /> @{status.ig_username}</>}
              </div>
            </div>
            <button
              onClick={disconnect}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
              data-testid="meta-disconnect-btn"
            >
              <Unlink className="h-3.5 w-3.5" /> Scollega
            </button>
          </div>
          {status.user_token_expires_at && (
            <div className="text-[11px] text-[#7c5a4e]">
              Token utente valido fino al: {new Date(status.user_token_expires_at).toLocaleDateString("it-IT")}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[#5a3a30]">
            Autorizza l'app a pubblicare su @ilpozzodeigiganti e sulla Pagina FB. Verrai reindirizzata a Facebook.
          </p>
          <button
            onClick={connect}
            disabled={connecting}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 via-fuchsia-500 to-indigo-500 text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:shadow disabled:opacity-60"
            data-testid="meta-connect-btn"
          >
            {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Connetti Instagram + Facebook
          </button>
        </div>
      )}
    </section>
  );
}

// -------------------- META DASHBOARD (monitoring) --------------------
function MetaDashboardSection() {
  const [days, setDays] = useState(7);
  const [overview, setOverview] = useState(null);
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // recent | views | engagement

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [o, p] = await Promise.all([
        axios.get(`${API}/admin/social/meta/overview?days=${days}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/social/meta/posts?limit=12`, { headers: authHeaders() }),
      ]);
      setOverview(o.data);
      setPosts(p.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        setErr("Collega prima Meta per vedere la dashboard.");
      } else {
        setErr(typeof detail === "string" ? detail : (detail?.error?.message || e.message));
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const sortedPosts = React.useMemo(() => {
    if (!posts?.instagram) return [];
    const arr = [...posts.instagram];
    if (sortBy === "views") {
      arr.sort((a, b) => (b.insights?.views || b.insights?.reach || 0) - (a.insights?.views || a.insights?.reach || 0));
    } else if (sortBy === "engagement") {
      arr.sort((a, b) => (b.insights?.total_interactions || 0) - (a.insights?.total_interactions || 0));
    } else {
      arr.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
    }
    return arr;
  }, [posts, sortBy]);

  return (
    <section className="rounded-2xl bg-white border border-[#e8dcc8] shadow-sm p-5 sm:p-6" data-testid="meta-dashboard">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#4a1c17] to-[#7a2c22] text-white flex items-center justify-center">
          <BarChart3 className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-lg font-bold text-[#2b1410]">Dashboard di monitoraggio</h3>
          <p className="text-xs text-[#7c5a4e]">Visualizzazioni, reach ed engagement dei tuoi post e reel</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-[#e8dcc8] px-2 py-1 text-xs bg-white"
          data-testid="dashboard-range-select"
        >
          <option value={7}>Ultimi 7 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
          <option value={90}>Ultimi 90 giorni</option>
        </select>
        <button
          onClick={load}
          className="rounded-lg border border-[#e8dcc8] p-1.5 hover:bg-[#f7ebdf]"
          title="Aggiorna"
          data-testid="dashboard-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 text-[#4a1c17] ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {err && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 mb-3">
          {err}
        </div>
      )}

      {/* KPI cards */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <KpiCard
            icon={<Instagram className="h-4 w-4" />}
            label="Follower IG"
            value={overview.instagram?.followers_count}
            hint={overview.instagram?.username ? `@${overview.instagram.username}` : ""}
            color="from-pink-500 to-fuchsia-600"
          />
          <KpiCard
            icon={<Eye className="h-4 w-4" />}
            label="Views IG"
            value={overview.instagram?.views}
            hint={`ultimi ${days}gg`}
            color="from-fuchsia-500 to-purple-600"
          />
          <KpiCard
            icon={<TrendingUp className="h-4 w-4" />}
            label="Reach IG"
            value={overview.instagram?.reach}
            hint={`ultimi ${days}gg`}
            color="from-purple-500 to-indigo-600"
          />
          <KpiCard
            icon={<Users className="h-4 w-4" />}
            label="Account raggiunti"
            value={overview.instagram?.accounts_engaged}
            hint={`ultimi ${days}gg`}
            color="from-indigo-500 to-blue-600"
          />
          <KpiCard
            icon={<Facebook className="h-4 w-4" />}
            label="Fan FB"
            value={overview.facebook?.fan_count ?? overview.facebook?.followers_count}
            hint="totale pagina"
            color="from-blue-500 to-cyan-600"
          />
          <KpiCard
            icon={<Eye className="h-4 w-4" />}
            label="Impressioni FB"
            value={overview.facebook?.page_impressions}
            hint={`ultimi ${days}gg`}
            color="from-cyan-500 to-teal-600"
          />
          <KpiCard
            icon={<Users className="h-4 w-4" />}
            label="Reach FB"
            value={overview.facebook?.page_impressions_unique}
            hint={`ultimi ${days}gg`}
            color="from-teal-500 to-emerald-600"
          />
          <KpiCard
            icon={<Heart className="h-4 w-4" />}
            label="Engagement FB"
            value={overview.facebook?.page_post_engagements}
            hint={`ultimi ${days}gg`}
            color="from-emerald-500 to-lime-600"
          />
        </div>
      )}

      {/* Posts grid */}
      {posts?.instagram && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-serif font-semibold text-[#2b1410]">
              Ultimi post ({posts.instagram.length})
            </h4>
            <div className="flex items-center gap-1 text-xs">
              <span className="text-[#7c5a4e]">Ordina:</span>
              {[["recent", "Recenti"], ["views", "Views"], ["engagement", "Engagement"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setSortBy(k)}
                  className={`px-2 py-0.5 rounded ${sortBy === k ? "bg-[#4a1c17] text-white" : "bg-[#f7ebdf] text-[#4a1c17]"}`}
                  data-testid={`sort-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {sortedPosts.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </div>
        </>
      )}

      {loading && !overview && (
        <div className="flex items-center justify-center py-8 text-[#7c5a4e]">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento metriche…
        </div>
      )}
    </section>
  );
}

function KpiCard({ icon, label, value, hint, color }) {
  return (
    <div className="rounded-xl border border-[#e8dcc8] bg-gradient-to-br from-white to-[#faf4ea] p-3">
      <div className={`inline-flex items-center gap-1.5 rounded-full text-white text-[10px] font-semibold px-2 py-0.5 mb-2 bg-gradient-to-r ${color}`}>
        {icon} {label}
      </div>
      <div className="font-serif text-2xl font-bold text-[#2b1410]">
        {value == null ? "—" : new Intl.NumberFormat("it-IT").format(value)}
      </div>
      {hint && <div className="text-[10px] text-[#7c5a4e] mt-0.5">{hint}</div>}
    </div>
  );
}

function PostCard({ post }) {
  const ins = post.insights || {};
  const isVideo = (post.media_type || "").toUpperCase() === "VIDEO" || (post.media_product_type || "").toUpperCase() === "REELS";
  const thumb = post.thumbnail_url || post.media_url;
  return (
    <a
      href={post.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="group rounded-xl overflow-hidden border border-[#e8dcc8] bg-white hover:shadow-md transition-shadow"
      data-testid={`post-card-${post.id}`}
    >
      <div className="relative aspect-square bg-neutral-100 overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            loading="lazy"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-neutral-400">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
        {isVideo && (
          <div className="absolute top-2 right-2 rounded-full bg-black/60 text-white px-2 py-0.5 text-[10px] flex items-center gap-1">
            <Video className="h-3 w-3" /> Reel
          </div>
        )}
        <div className="absolute bottom-2 right-2 rounded-full bg-black/60 text-white p-1">
          <ExternalLink className="h-3 w-3" />
        </div>
      </div>
      <div className="p-2.5 space-y-1">
        <div className="text-[10px] text-[#7c5a4e]">
          {post.timestamp ? new Date(post.timestamp).toLocaleDateString("it-IT", { day: "numeric", month: "short" }) : ""}
        </div>
        {(post.caption || "").trim() && (
          <div className="text-xs text-[#2b1410] line-clamp-2">
            {post.caption}
          </div>
        )}
        <div className="grid grid-cols-3 gap-1 pt-1 text-[10px] text-[#4a1c17]">
          {ins.views != null && (
            <Stat icon={<Eye className="h-3 w-3" />} val={ins.views} label="views" />
          )}
          {ins.reach != null && (
            <Stat icon={<TrendingUp className="h-3 w-3" />} val={ins.reach} label="reach" />
          )}
          {ins.likes != null && (
            <Stat icon={<Heart className="h-3 w-3" />} val={ins.likes} label="like" />
          )}
          {ins.comments != null && (
            <Stat icon={<MessageCircle className="h-3 w-3" />} val={ins.comments} label="cmt" />
          )}
          {ins.saved != null && (
            <Stat icon={<Bookmark className="h-3 w-3" />} val={ins.saved} label="save" />
          )}
          {ins.shares != null && (
            <Stat icon={<Share2 className="h-3 w-3" />} val={ins.shares} label="share" />
          )}
        </div>
      </div>
    </a>
  );
}

function Stat({ icon, val, label }) {
  return (
    <div className="flex items-center gap-1 rounded-md bg-[#f7ebdf] px-1.5 py-1" title={label}>
      {icon}
      <span className="font-semibold">{new Intl.NumberFormat("it-IT", { notation: "compact" }).format(val)}</span>
    </div>
  );
}

// -------------------- AI GENERATOR --------------------
function GeneratorSection() {
  const [pillar, setPillar] = useState("territorio");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const generate = async () => {
    setErr("");
    setResult(null);
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/social/generate-caption`,
        { pillar, description: description.trim() },
        { headers: authHeaders() }
      );
      setResult(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Errore AI");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl bg-white border border-[#e8dcc8] shadow-sm p-5 sm:p-6" data-testid="social-generator">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#c48b31] to-[#a56c1e] text-white flex items-center justify-center">
          <Wand2 className="h-4 w-4" />
        </div>
        <div>
          <h3 className="font-serif text-lg font-bold text-[#2b1410]">Generatore AI — Prompt Artlist + Caption</h3>
          <p className="text-xs text-[#7c5a4e]">Descrivi l'idea → ottieni prompt Artlist Toolkit + caption IT/EN + hashtag pronti</p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mb-3">
        {PILLARS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPillar(p.key)}
            data-testid={`pillar-${p.key}`}
            className={`text-left rounded-xl border-2 p-3 transition-all ${
              pillar === p.key
                ? "border-[#4a1c17] bg-[#faf3ee]"
                : "border-[#e8dcc8] bg-white hover:border-[#4a1c17]/40"
            }`}
          >
            <div className="text-sm font-semibold text-[#2b1410]">{p.label}</div>
            <div className="text-[11px] text-[#7c5a4e] mt-0.5">{p.hint}</div>
          </button>
        ))}
      </div>

      <label className="block text-xs font-semibold text-[#4a1c17] mb-1">
        Descrivi il contenuto (foto/video che hai in mente)
      </label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Es: 'cavalli al tramonto tra ulivi marchigiani, momento di pace' — oppure lascia vuoto per generazione libera nel pilastro scelto"
        rows={3}
        className="w-full rounded-xl border border-[#e8dcc8] p-3 text-sm focus:outline-none focus:border-[#4a1c17] resize-none"
        data-testid="generator-description"
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={generate}
          disabled={busy}
          data-testid="generator-run"
          className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] disabled:opacity-60 text-[#f7ebdf] px-5 py-2.5 text-sm font-semibold transition-colors"
        >
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Genero…</> : <><Sparkles className="h-4 w-4" /> Genera</>}
        </button>
        <p className="text-xs text-[#7c5a4e] italic">
          <Info className="inline h-3 w-3 mr-1" />
          Ogni generazione consuma ~2000 token del piano LLM (~€0,002)
        </p>
      </div>

      {err && (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3">
          {err}
        </div>
      )}

      {result && <ResultCards result={result} />}
    </section>
  );
}

function ResultCards({ result }) {
  const [copied, setCopied] = useState(null);
  const copy = (key, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    });
  };
  return (
    <div className="mt-5 space-y-3" data-testid="generator-result">
      {/* Artlist Prompt */}
      <ResultBlock
        title="🎬 Prompt per Artlist Toolkit"
        subtitle="Copialo e incollalo nel campo prompt di Artlist per generare il video"
        text={result.artlist_prompt}
        copyKey="artlist"
        copied={copied}
        onCopy={copy}
        accent="from-[#7c2d20] to-[#4a1c17]"
        monospace
      />
      {/* Caption IT */}
      <ResultBlock
        title="📝 Caption Italiano"
        text={result.caption_it}
        copyKey="cap_it"
        copied={copied}
        onCopy={copy}
        accent="from-[#4a1c17] to-[#2b1410]"
      />
      {/* Caption EN */}
      <ResultBlock
        title="📝 Caption English"
        text={result.caption_en}
        copyKey="cap_en"
        copied={copied}
        onCopy={copy}
        accent="from-[#1c3d7c] to-[#2b5eb0]"
      />
      {/* Hashtag */}
      <div className="rounded-xl bg-gradient-to-br from-[#c48b31] to-[#a56c1e] text-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs font-semibold uppercase tracking-widest opacity-90">🏷️ Hashtag</div>
          <button
            onClick={() => copy("tags", result.hashtags.join(" "))}
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 px-2 py-1 text-[11px] font-semibold"
          >
            {copied === "tags" ? <><Check className="h-3 w-3" /> Copiato</> : <><Copy className="h-3 w-3" /> Copia tutti</>}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {result.hashtags.map((h) => (
            <span key={h} className="inline-block rounded-full bg-white/15 border border-white/25 px-2 py-0.5 text-xs font-medium">
              {h}
            </span>
          ))}
        </div>
      </div>

      {/* Copy ALL bundle */}
      <button
        onClick={() =>
          copy(
            "all",
            `${result.caption_it}\n\n—\n\n${result.caption_en}\n\n${result.hashtags.join(" ")}`
          )
        }
        data-testid="generator-copy-all"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#faf3ee] hover:bg-[#f7ebdf] border-2 border-[#c48b31] text-[#4a1c17] px-4 py-2.5 text-sm font-semibold"
      >
        {copied === "all" ? <><Check className="h-4 w-4" /> Post completo copiato!</> : <><Copy className="h-4 w-4" /> Copia POST COMPLETO (IT + EN + hashtag)</>}
      </button>
    </div>
  );
}

function ResultBlock({ title, subtitle, text, copyKey, copied, onCopy, accent, monospace }) {
  return (
    <div className={`rounded-xl bg-gradient-to-br ${accent} text-white p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-xs font-semibold uppercase tracking-widest opacity-90">{title}</div>
        <button
          onClick={() => onCopy(copyKey, text)}
          className="ml-auto inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 px-2 py-1 text-[11px] font-semibold"
          data-testid={`copy-${copyKey}`}
        >
          {copied === copyKey ? <><Check className="h-3 w-3" /> Copiato</> : <><Copy className="h-3 w-3" /> Copia</>}
        </button>
      </div>
      {subtitle && <p className="text-[11px] opacity-80 mb-2">{subtitle}</p>}
      <p className={`text-sm leading-relaxed whitespace-pre-wrap ${monospace ? "font-mono text-xs" : ""}`}>
        {text}
      </p>
    </div>
  );
}

// -------------------- LIBRARY / UPLOAD --------------------
function LibrarySection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPillar, setUploadPillar] = useState("territorio");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/social/content`, { headers: authHeaders() });
      setItems(res.data.items || []);
    } catch (e) {
      console.warn("load library", e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const f of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("pillar", uploadPillar);
        fd.append("description", "");
        await axios.post(`${API}/admin/social/content`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        console.warn("upload failed", f.name, e);
      }
    }
    setUploading(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo contenuto? L'operazione è irreversibile.")) return;
    try {
      await axios.delete(`${API}/admin/social/content/${id}`, { headers: authHeaders() });
      load();
    } catch (e) {
      alert("Errore: " + e.message);
    }
  };

  return (
    <section className="rounded-2xl bg-white border border-[#e8dcc8] shadow-sm p-5 sm:p-6" data-testid="social-library">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-full bg-[#4a1c17] text-[#f7ebdf] flex items-center justify-center">
          <Video className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-lg font-bold text-[#2b1410]">Libreria contenuti</h3>
          <p className="text-xs text-[#7c5a4e]">
            Carica video/foto (max 100MB). Dopo il caricamento puoi aggiungere caption e programmarli.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs font-medium text-[#4a1c17] hover:bg-[#faf3ee]"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Ricarica
        </button>
      </div>

      {/* Upload zone */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-[#4a1c17]">Pilastro nuovi upload:</span>
          {PILLARS.map((p) => (
            <button
              key={p.key}
              onClick={() => setUploadPillar(p.key)}
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                uploadPillar === p.key
                  ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]"
                  : "bg-white text-[#4a1c17] border-[#e8dcc8] hover:bg-[#faf3ee]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label
          className="block rounded-xl border-2 border-dashed border-[#c48b31] bg-[#faf3ee] hover:bg-[#f7ebdf] p-5 sm:p-6 text-center cursor-pointer transition-colors"
          data-testid="upload-dropzone"
        >
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(e) => onFiles(e.target.files)}
            className="hidden"
            data-testid="upload-input"
          />
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#4a1c17]" />
              <p className="mt-2 text-sm text-[#4a1c17]">Caricamento in corso…</p>
            </>
          ) : (
            <>
              <Upload className="h-6 w-6 mx-auto text-[#c48b31]" />
              <p className="mt-2 text-sm text-[#4a1c17] font-semibold">Clicca per caricare foto/video</p>
              <p className="text-xs text-[#7c5a4e]">Selezione multipla ok · max 100MB per file</p>
            </>
          )}
        </label>
      </div>

      {/* Items grid */}
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#7c5a4e]">
          Nessun contenuto ancora. Carica il primo video/foto qui sopra.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="library-grid">
          {items.map((it) => (
            <ContentCard
              key={it.id}
              item={it}
              onEdit={() => setEditing(it)}
              onDelete={() => remove(it.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <EditModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
          }}
        />
      )}
    </section>
  );
}

function ContentCard({ item, onEdit, onDelete }) {
  const statusMeta = STATUS_META[item.status] || STATUS_META.draft;
  const isVideo = item.kind === "video";
  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${item.file_url}`;
  return (
    <div className="rounded-xl border border-[#e8dcc8] bg-white overflow-hidden shadow-sm">
      <div className="relative bg-neutral-100 aspect-video">
        {isVideo ? (
          <video src={fileUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        ) : (
          <img src={fileUrl} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
          {isVideo ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
          {item.kind}
        </div>
        <div className={`absolute top-2 right-2 inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusMeta.cls}`}>
          {statusMeta.label}
        </div>
      </div>
      <div className="p-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-[#c48b31]">
          {PILLARS.find((p) => p.key === item.pillar)?.label || item.pillar}
        </div>
        <p className="mt-1 text-sm text-[#2b1410] line-clamp-3 min-h-[3rem]">
          {item.caption_it || <span className="italic text-[#7c5a4e]">Nessuna caption ancora — modifica per aggiungerla</span>}
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={onEdit}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-2 py-1.5 text-xs font-semibold"
            data-testid={`edit-${item.id}`}
          >
            <Edit3 className="h-3 w-3" /> Modifica
          </button>
          <button
            onClick={onDelete}
            className="inline-flex items-center justify-center rounded-full bg-white border border-red-200 hover:bg-red-50 text-red-600 h-7 w-7"
            title="Elimina"
            data-testid={`delete-${item.id}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ item, onClose, onSaved }) {
  const [captionIt, setCaptionIt] = useState(item.caption_it || "");
  const [captionEn, setCaptionEn] = useState(item.caption_en || "");
  const [tags, setTags] = useState((item.hashtags || []).join(" "));
  const [pillar, setPillar] = useState(item.pillar || "territorio");
  const [description, setDescription] = useState(item.description || "");
  const [status, setStatus] = useState(item.status || "draft");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [brandBusy, setBrandBusy] = useState(false);
  const [brandV, setBrandV] = useState(item.updated_at || 0);
  const [brandMark, setBrandMark] = useState(item.brand_mark || null);
  const isVideo = item.kind === "video";
  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${item.file_url}${item.file_url?.includes('?') ? '&' : '?'}v=${brandV}`;

  const applyBrand = async () => {
    if (isVideo) {
      alert("Il brand mark è disponibile solo per le foto. Per i video useremo un template diverso.");
      return;
    }
    if (brandMark) {
      const ok = window.confirm(
        "Questa foto ha già il brand mark applicato. Riapplicarlo di nuovo potrebbe sovrapporre 2 firme. Continuare?"
      );
      if (!ok) return;
    }
    setBrandBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/social/content/${item.id}/apply-brand`,
        {},
        { headers: authHeaders() }
      );
      setBrandMark(res.data.brand_mark || "subtle");
      setBrandV(res.data.updated_at || Date.now());
      onSaved(res.data);
    } catch (e) {
      alert("Errore brand mark: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBrandBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await axios.patch(
        `${API}/admin/social/content/${item.id}`,
        {
          caption_it: captionIt,
          caption_en: captionEn,
          hashtags: tags.split(/\s+/).map((s) => s.trim()).filter(Boolean),
          pillar,
          status,
        },
        { headers: authHeaders() }
      );
      onSaved(res.data);
    } catch (e) {
      alert("Errore salvataggio: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const runAI = async () => {
    setAiBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/social/generate-caption`,
        { pillar, description: description.trim() },
        { headers: authHeaders() }
      );
      setCaptionIt(res.data.caption_it);
      setCaptionEn(res.data.caption_en);
      setTags(res.data.hashtags.join(" "));
    } catch (e) {
      alert("AI errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-modal"
      >
        <div className="sticky top-0 bg-[#4a1c17] text-[#f7ebdf] px-5 py-3 flex items-center justify-between rounded-t-2xl">
          <div className="font-serif text-lg font-semibold">Modifica contenuto</div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="relative rounded-xl overflow-hidden bg-neutral-100 aspect-video max-h-64">
            {isVideo ? (
              <video src={fileUrl} className="w-full h-full object-contain" controls playsInline />
            ) : (
              <img src={fileUrl} alt="" className="w-full h-full object-contain" />
            )}
            {brandMark && !isVideo && (
              <div className="absolute top-2 right-2 rounded-full bg-emerald-600 text-white text-[10px] font-semibold px-2 py-0.5 shadow-md">
                ✓ Firmata
              </div>
            )}
          </div>

          {/* Brand mark action — subtle logo + handle overlay */}
          {!isVideo && (
            <button
              type="button"
              onClick={applyBrand}
              disabled={brandBusy}
              data-testid="apply-brand-btn"
              className={`w-full rounded-xl border-2 border-dashed py-2.5 text-sm font-semibold transition-colors ${
                brandMark
                  ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : "border-[#c48b31] bg-[#faf3ee] text-[#4a1c17] hover:bg-[#f7ebdf]"
              } disabled:opacity-50 disabled:cursor-wait`}
            >
              {brandBusy
                ? "Applico firma…"
                : brandMark
                ? "🎨 Riapplica firma discreta (logo)"
                : "🎨 Applica firma discreta (logo)"}
            </button>
          )}

          <div>
            <label className="block text-xs font-semibold text-[#4a1c17] mb-1">Pilastro</label>
            <div className="flex gap-2 flex-wrap">
              {PILLARS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPillar(p.key)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                    pillar === p.key ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white text-[#4a1c17] border-[#e8dcc8]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4a1c17] mb-1">Descrizione (per AI)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Cosa mostra il video/foto? es: 'cavalli al tramonto tra ulivi'"
              className="w-full rounded-xl border border-[#e8dcc8] p-2 text-sm resize-none focus:outline-none focus:border-[#4a1c17]"
            />
            <button
              onClick={runAI}
              disabled={aiBusy}
              className="mt-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-[#c48b31] to-[#a56c1e] text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
              data-testid="modal-ai-generate"
            >
              {aiBusy ? <><Loader2 className="h-3 w-3 animate-spin" /> Genero…</> : <><Wand2 className="h-3 w-3" /> Genera caption + hashtag con AI</>}
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4a1c17] mb-1">Caption IT</label>
            <textarea
              value={captionIt}
              onChange={(e) => setCaptionIt(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-[#e8dcc8] p-2 text-sm resize-none focus:outline-none focus:border-[#4a1c17]"
              data-testid="modal-caption-it"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4a1c17] mb-1">Caption EN</label>
            <textarea
              value={captionEn}
              onChange={(e) => setCaptionEn(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-[#e8dcc8] p-2 text-sm resize-none focus:outline-none focus:border-[#4a1c17]"
              data-testid="modal-caption-en"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4a1c17] mb-1">Hashtag (separati da spazio)</label>
            <textarea
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              rows={2}
              className="w-full rounded-xl border border-[#e8dcc8] p-2 text-sm resize-none focus:outline-none focus:border-[#4a1c17]"
              data-testid="modal-tags"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#4a1c17] mb-1">Stato</label>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(STATUS_META).map(([k, m]) => (
                <button
                  key={k}
                  onClick={() => setStatus(k)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                    status === k ? m.cls + " ring-2 ring-offset-1 ring-[#4a1c17]" : "bg-white text-[#7c5a4e] border-[#e8dcc8]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Auto-publish direct to Meta (requires connection) */}
          <MetaPublishButtons
            item={item}
            captionIt={captionIt}
            captionEn={captionEn}
            tags={tags}
            onPublished={(updated) => {
              setStatus("published");
              onSaved(updated);
            }}
          />

          {/* Quick share buttons */}
          <div className="rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-3">
            <p className="text-xs font-semibold text-[#4a1c17] mb-2 flex items-center gap-1">
              <Send className="h-3 w-3" /> Pubblica manualmente (copia+incolla su Instagram/Facebook)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${captionIt}\n\n—\n\n${captionEn}\n\n${tags}`);
                  alert("Post copiato ✅ Vai su Instagram e incolla nella caption");
                  window.open("https://www.instagram.com/", "_blank");
                }}
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-[#7c2d20] to-[#c48b31] text-white px-3 py-2 text-xs font-semibold"
              >
                <Instagram className="h-3.5 w-3.5" /> Copia + Apri IG
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${captionIt}\n\n—\n\n${captionEn}\n\n${tags}`);
                  alert("Post copiato ✅ Vai sulla Pagina FB e incolla nel post");
                  window.open("https://www.facebook.com/profile.php?id=61572083007558", "_blank");
                }}
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-[#1c3d7c] to-[#2b5eb0] text-white px-3 py-2 text-xs font-semibold"
              >
                <Facebook className="h-3.5 w-3.5" /> Copia + Apri FB
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#e8dcc8]">
            <button onClick={onClose} className="rounded-full border border-[#e8dcc8] bg-white px-4 py-2 text-sm">
              Annulla
            </button>
            <button
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm font-semibold disabled:opacity-60"
              data-testid="modal-save"
            >
              {busy ? <><Loader2 className="h-3 w-3 animate-spin" /> Salvo…</> : <><Check className="h-3 w-3" /> Salva</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// -------------------- META AUTO-PUBLISH BUTTONS --------------------
function MetaPublishButtons({ item, captionIt, captionEn, tags, onPublished }) {
  const [busy, setBusy] = useState(null); // "instagram" | "facebook" | "both"
  const [status, setStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/social/meta/status`, { headers: authHeaders() })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ connected: false }));
  }, []);

  const publish = async (target) => {
    // Persist current caption edits first
    setBusy(target);
    try {
      await axios.patch(
        `${API}/admin/social/content/${item.id}`,
        {
          caption_it: captionIt,
          caption_en: captionEn,
          hashtags: tags.split(/\s+/).map((s) => s.trim()).filter(Boolean),
        },
        { headers: authHeaders() }
      );
      const res = await axios.post(
        `${API}/admin/social/meta/publish/${item.id}`,
        { target },
        { headers: authHeaders(), timeout: 240000 } // 4 min for reels
      );
      const results = res.data?.results || {};
      const igErr = results.instagram_error;
      const fbErr = results.facebook_error;
      if (igErr || fbErr) {
        const detail = JSON.stringify(igErr || fbErr).slice(0, 300);
        alert(`Pubblicazione parziale.\nIG: ${results.instagram ? "OK" : (igErr ? "errore" : "-")}\nFB: ${results.facebook ? "OK" : (fbErr ? "errore" : "-")}\n\n${detail}`);
      } else {
        alert("✅ Pubblicato con successo!");
      }
      onPublished(res.data.content);
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert("Errore pubblicazione: " + (typeof d === "string" ? d : JSON.stringify(d || e.message).slice(0, 400)));
    } finally {
      setBusy(null);
    }
  };

  if (!status) return null;
  if (!status.connected) {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-center gap-2">
        <Info className="h-4 w-4" />
        Per pubblicare automaticamente, collega prima Meta (sezione in alto).
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-gradient-to-br from-pink-50 to-indigo-50 border border-pink-200 p-3">
      <p className="text-xs font-semibold text-[#4a1c17] mb-2 flex items-center gap-1">
        <Send className="h-3 w-3" /> Pubblica automaticamente adesso
      </p>
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => publish("instagram")}
          disabled={!!busy}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-600 text-white px-3 py-2 text-xs font-semibold disabled:opacity-60"
          data-testid="publish-ig-btn"
        >
          {busy === "instagram" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Instagram className="h-3.5 w-3.5" />}
          {item.kind === "video" ? "Reel IG" : "IG"}
        </button>
        <button
          onClick={() => publish("facebook")}
          disabled={!!busy}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white px-3 py-2 text-xs font-semibold disabled:opacity-60"
          data-testid="publish-fb-btn"
        >
          {busy === "facebook" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Facebook className="h-3.5 w-3.5" />}
          FB
        </button>
        <button
          onClick={() => publish("both")}
          disabled={!!busy}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-[#4a1c17] to-[#7a2c22] text-white px-3 py-2 text-xs font-semibold disabled:opacity-60"
          data-testid="publish-both-btn"
        >
          {busy === "both" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Entrambi
        </button>
      </div>
      {item.kind === "video" && (
        <p className="text-[10px] text-[#7c5a4e] mt-2">
          ⏱️ I reel richiedono 30-120 sec di elaborazione lato Meta prima della pubblicazione.
        </p>
      )}
    </div>
  );
}